// TimeCards.js

import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  Box,
  Flex,
  Text,
  VStack,
  Spinner,
  Input,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Checkbox,
  useToast,
  Link,
  Heading,
} from "@chakra-ui/react";
import moment from "moment";
import { cqAPI, getBid } from "../../lib/Auth";
import { useParams } from 'react-router-dom';
import Select from 'react-select';
import SignaturePad from 'signature_pad';

function TimeCards() {
  const [times, setTimes] = useState([]);
  const [date, setDate] = useState(moment().format('L')); // 'MM/DD/YYYY'
  const [isLoading, setIsLoading] = useState(false);
  const [staff, setStaff] = useState({});
  const [payroll, setPayroll] = useState([]);
  const [range, setRange] = useState({});
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [currentAttendance, setCurrentAttendance] = useState(null);
  const [clockedin, setClockedin] = useState('');
  const [clockedout, setClockedout] = useState('');
  const [note, setNote] = useState('');
  const [staffList, setStaffList] = useState([]);
  const [selectedStaffId, setSelectedStaffId] = useState('');
  const [selectedDateForAdd, setSelectedDateForAdd] = useState('');
  const [selectedStaff, setSelectedStaff] = useState({});
  const [csvData, setCsvData] = useState('');
  const toast = useToast();
  const isMe = window.location.pathname.includes('me');
  const { period } = useParams();
  const bid = getBid()

  // Signature pad related states and refs
  const signatureCanvasRef = useRef(null);
  const [signaturePad, setSignaturePad] = useState(null);
  const [isSignatureEmpty, setIsSignatureEmpty] = useState(true);
  const [isSigned, setIsSigned] = useState(false);
  const [isLoadingSignature, setIsLoadingSignature] = useState(false);

  const periodEnd = range.end ? new Date(range.end).getTime() : 0;

  const fetchTimes = async () => {
    setIsLoading(true);
    try {
      const response = await cqAPI.get(
        `/timesheet` +
          (isMe
            ? /^\d+$/.test(period)?`?me=1&period=${period}`:`?me=1`
            : `?date=${encodeURIComponent(date)}`)
      );
      if (response.type === "ok") {
        const timesWithEditableFlag = (response.data.times || []).map(
          (time) => ({
            ...time,
            editable: true, // Adjust logic based on your application's permissions
          })
        );
        setTimes(timesWithEditableFlag);
        setStaff(response.data.staff || {});
        setPayroll(response.data.payroll || []);
        setRange(response.data.range || {});

        // Update isSigned state
        if (isMe && response.data.staff && response.data.payroll) {
          const myStaffMember = Object.values(response.data.staff)[0];
          const myPay = response.data.payroll.find(
            (p) => p.uid === myStaffMember.uid
          );
          setIsSigned(!!myPay && !!myPay.signature);
        }
      } else {
        console.error("Error fetching times");
      }
    } catch (err) {
      console.error("Error fetching times:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStaffList = async () => {
    try {
      const response = await cqAPI.get('/list-staff');
      if (response.type === 'ok') {
        setStaffList(response.data || []);
      } else {
        console.error("Error fetching staff list");
      }
    } catch (err) {
      console.error("Error fetching staff list:", err);
    }
  };

  useEffect(() => {
    fetchTimes();
    fetchStaffList();
  }, []);

  // Inside your TimeCards component
  const groupedTimes = useMemo(() => {
    const groups = {};

    // Ensure range is defined
    if (range && range.start && range.end) {
      const startDate = moment(range.start);
      const endDate = moment(range.end);
      let currentDate = startDate.clone();

      // Generate all dates between start and end dates
      while (currentDate.isSameOrBefore(endDate, 'day')) {
        const dateStr = currentDate.format('dddd MMMM Do');
        groups[dateStr] = []; // Initialize with empty array
        currentDate.add(1, 'day');
      }
    }

    // Populate groups with existing times
    times.forEach((time) => {
      const dateStr = moment(time.clockedin).format('dddd MMMM Do');
      if (!groups[dateStr]) {
        groups[dateStr] = [];
      }
      groups[dateStr].push(time);
    });

    return groups;
  }, [times, range]);
  

  const handleEdit = (time) => {
    setCurrentAttendance(time);
    setIsEditModalOpen(true);
  };

  const handleAdd = (dateStr) => {
    setSelectedDateForAdd(dateStr);
    setSelectedStaffId('');
    setClockedin('');
    setClockedout('');
    setNote('');
    setIsAddModalOpen(true);
  };

  useEffect(() => {
    if (currentAttendance) {
      setClockedin(moment(currentAttendance.clockedin).format('h:mm a'));
      setClockedout(moment(currentAttendance.clockedout).format('h:mm a'));
      setNote(currentAttendance.note || '');
    }
  }, [currentAttendance]);

  const handleUpdate = async () => {
    const dateStr = moment(currentAttendance.clockedin).format('YYYY-MM-DD');
    const clockedinTime = moment(
      `${dateStr} ${clockedin}`,
      ['YYYY-MM-DD h:mm a', 'YYYY-MM-DD H:mm']
    ).toISOString();
    const clockedoutTime = moment(
      `${dateStr} ${clockedout}`,
      ['YYYY-MM-DD h:mm a', 'YYYY-MM-DD H:mm']
    ).toISOString();

    try {
      const response = await cqAPI.post('/updateclockin', {
        id: currentAttendance._id,
        clockedin: clockedinTime,
        clockedout: clockedoutTime,
        note,
      });
      if (response.type === 'ok') {
        await fetchTimes();
        setIsEditModalOpen(false);
        toast({
          title: "Attendance updated.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } else {
        console.error("Error updating attendance");
        toast({
          title: "Error updating attendance.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (err) {
      console.error("Error updating attendance:", err);
      toast({
        title: "Error updating attendance.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleDelete = async () => {
    if (
      !window.confirm(
        'This action is not reversible. Are you sure you want to delete this record?'
      )
    ) {
      return;
    }
    try {
      const response = await cqAPI.get(
        '/removeclockin?id=' + currentAttendance._id
      );
      if (response.type === 'ok') {
        await fetchTimes();
        setIsEditModalOpen(false);
        toast({
          title: "Attendance deleted.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } else {
        console.error("Error deleting attendance");
        toast({
          title: "Error deleting attendance.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (err) {
      console.error("Error deleting attendance:", err);
      toast({
        title: "Error deleting attendance.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleAddAttendance = async () => {
    if (!selectedStaffId || !clockedin || !clockedout) {
      toast({
        title: "Please fill in all required fields.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const selectedStaffMember = staffList.find(
      (s) => s._id === selectedStaffId
    );
    if (!selectedStaffMember) {
      toast({
        title: "Selected staff member not found.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const dateStr = moment(selectedDateForAdd, 'dddd MMMM Do').format(
      'YYYY-MM-DD'
    );
    const clockedinTime = moment(
      `${dateStr} ${clockedin}`,
      ['YYYY-MM-DD h:mm a', 'YYYY-MM-DD H:mm']
    ).toISOString();
    const clockedoutTime = moment(
      `${dateStr} ${clockedout}`,
      ['YYYY-MM-DD h:mm a', 'YYYY-MM-DD H:mm']
    ).toISOString();

    try {
      const response = await cqAPI.post('/addclockin', {
        clockedin: clockedinTime,
        clockedout: clockedoutTime,
        uid: selectedStaffId,
        note,
      });
      if (response.type === 'ok') {
        await fetchTimes();
        setIsAddModalOpen(false);
        toast({
          title: "Attendance added.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } else {
        console.error("Error adding attendance");
        toast({
          title: "Error adding attendance.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (err) {
      console.error("Error adding attendance:", err);
      toast({
        title: "Error adding attendance.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleStaffSelection = (uid) => {
    setSelectedStaff((prevState) => ({
      ...prevState,
      [uid]: !prevState[uid],
    }));
  };

  const generateCSVData = () => {
    const selectedStaffData = Object.keys(staff)
      .filter((name) => selectedStaff[staff[name].uid])
      .map((name) => {
        const staffMember = staff[name];
        const firstName = name.split(' ')[0];
        const lastName = name.split(' ').slice(1).join(' ');
        return {
          first_name: firstName,
          last_name: lastName,
          regular_hours: staffMember.exempt
            ? 'N/A'
            : (staffMember.hours / 60).toFixed(2),
          overtime_hours: staffMember.exempt
            ? 'N/A'
            : (staffMember.ot / 60).toFixed(2),
          double_overtime_hours: staffMember.exempt
            ? 'N/A'
            : (staffMember.ott / 60).toFixed(2),
          bonus: staffMember.bonus || '0.00',
        };
      });

    let csvContent =
      'first_name,last_name,regular_hours,overtime_hours,double_overtime_hours,bonus\n';
    selectedStaffData.forEach((row) => {
      csvContent += `${row.first_name},${row.last_name},${row.regular_hours},${row.overtime_hours},${row.double_overtime_hours},${row.bonus}\n`;
    });

    const blob = new Blob([csvContent], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);
    setCsvData(url);
  };

  useEffect(() => {
    generateCSVData();
    return () => {
      if (csvData) {
        URL.revokeObjectURL(csvData);
      }
    };
  }, [selectedStaff]);

  const requestSignature = async (uid) => {
    try {
      const periodStart = new Date(range.start).getTime() + 1000; // Adjust as per original code
      const response = await cqAPI.post('/signpayroll', {
        uid,
        period: periodStart,
      });
      if (response.type === 'ok') {
        fetchTimes();
        toast({
          title: "Signature requested.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (err) {
      console.error("Error requesting signature:", err);
    }
  };

  const handleClearSignature = () => {
    if (signaturePad) {
      signaturePad.clear();
      setIsSignatureEmpty(true);
    }
  };

  const handleSaveSignature = async () => {
    setIsLoadingSignature(true);
    try {
      const signatureData = signaturePad.toDataURL();
      const response = await cqAPI.post(`/store_signature/${period}`, {
        signature: signatureData.split(',')[1],
      });
      if (response.type === 'ok') {
        setIsSigned(true);
        toast({
          title: 'Timesheet signed successfully.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        toast({
          title: 'Error signing timesheet.',
          description: response.msg || 'An unexpected error occurred.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error in handleSaveSignature:', error);
      toast({
        title: 'Error signing timesheet.',
        description: 'An unexpected error occurred.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoadingSignature(false);
    }
  };

  // Initialize Signature Pad
  useEffect(() => {
    if (
      isMe &&
      new Date().getTime() > periodEnd &&
      !isSigned &&
      signatureCanvasRef.current
    ) {
      const canvas = signatureCanvasRef.current;
      const pad = new SignaturePad(canvas);
      setSignaturePad(pad);

      // Set initial isSignatureEmpty state
      setIsSignatureEmpty(pad.isEmpty());

      // Resize the canvas to match the container
      const resizeCanvas = () => {
              const ratio = Math.max(window.devicePixelRatio || 1, 1);

              let data;
              if (!pad.isEmpty()) {
                data = pad.toDataURL();
              }

              canvas.width = canvas.offsetWidth * ratio;
              canvas.height = canvas.offsetHeight * ratio;
              canvas.getContext('2d').scale(ratio, ratio);

              if (data) {
                pad.fromDataURL(data);
              } else {
                pad.clear();
              }

              // Update isSignatureEmpty state
              setIsSignatureEmpty(pad.isEmpty());
            };

      resizeCanvas(); // Initialize on mount
      window.addEventListener('resize', resizeCanvas);

      // Add event listener to track signature pad changes
      const handleBeginStroke = () => {
        setIsSignatureEmpty(false);
      };
      pad.addEventListener('beginStroke', handleBeginStroke);

      return () => {
        window.removeEventListener('resize', resizeCanvas);
        pad.removeEventListener('beginStroke', handleBeginStroke);
        // No need to call pad.off() as SignaturePad doesn't have this method
      };
    }
  }, [isMe, periodEnd, isSigned]);

  if (isLoading) {
    return (
      <Flex
        justifyContent="center"
        alignItems="center"
        height="100%"
        width="100%"
        position="relative"
      >
        <Spinner color="blue.500" size="xl" />
      </Flex>
    );
  }

  return (
    <Box p={4} overflowY="auto" maxHeight="100vh" width="100%">
      {!isMe && (
        <Flex mb={4} alignItems="center">
          <Text mr={2}>Date:</Text>
          <Input
            value={date}
            onChange={(e) => setDate(e.target.value)}
            width="200px"
          />
          <Button onClick={fetchTimes} ml={2}>
            Submit
          </Button>
        </Flex>
      )}

      {/* Timesheet Entries */}
      {Object.keys(groupedTimes).length > 0 ? (
        <VStack spacing={4} align="stretch">
          {Object.keys(groupedTimes).map((dateKey) => (
            <Box key={dateKey}>
              {!isMe ? (
                <Flex
                  borderBottom="1px"
                  borderColor="gray.200"
                  p={2}
                  alignItems="center"
                >
                  <Text fontWeight="bold" flex="1">
                    {dateKey}
                  </Text>
                  <Button size="sm" onClick={() => handleAdd(dateKey)}>
                    Add
                  </Button>
                </Flex>
              ):(
              <Flex
                borderBottom="1px"
                borderColor="gray.200"
                p={2}
                alignItems="center"
              >
                <Text fontWeight="bold" flex="1">
                  {dateKey}
                </Text>
              </Flex>
              )}
              {/* Header Row */}
              <Flex p={2} bg="gray.100" fontWeight="bold">
                <Box flexBasis="15%">Name</Box>
                <Box flexBasis="15%">Office</Box>
                <Box flexBasis="10%">In</Box>
                <Box flexBasis="10%">Out</Box>
                <Box flexBasis="15%">Modified by</Box>
                <Box flexBasis="25%">Note</Box>
                {!isMe && <Box flexBasis="10%">Action</Box>}
              </Flex>
              {groupedTimes[dateKey].map((time, index) => (
                <Flex key={index} p={2} alignItems="center">
                  <Box flexBasis="15%">{time.name}</Box>
                  <Box flexBasis="15%">{time.office}</Box>
                  <Box flexBasis="10%">
                    {moment(time.clockedin).format('h:mm a')}
                  </Box>
                  <Box flexBasis="10%">
                    {moment(time.clockedout).format('h:mm a')}
                  </Box>
                  <Box flexBasis="15%">{time.modified_by}</Box>
                  <Box flexBasis="25%">{time.note}</Box>
                  {!isMe && (
                    <Flex flexBasis="10%">
                      {time.editable && (
                        <Button size="sm" onClick={() => handleEdit(time)}>
                          Update
                        </Button>
                      )}
                    </Flex>
                  )}
                </Flex>
              ))}
            </Box>
          ))}
        </VStack>
      ) : (
        <Text>No attendance records found for the selected date.</Text>
      )}

      {/* Staff Totals Section */}
      {Object.keys(staff).length > 0 && (
        <Box mt={8}>
          <Flex
            borderBottom="1px"
            borderColor="gray.200"
            p={2}
            alignItems="center"
          >
            <Text fontWeight="bold">{isMe ? 'Total' : 'Staff Totals'}</Text>
          </Flex>
          {/* Header Row */}
          <Flex p={2} bg="gray.100" fontWeight="bold">
            <Box flexBasis="20%">Name</Box>
            <Box flexBasis="10%">Hours</Box>
            <Box flexBasis="10%">OT</Box>
            <Box flexBasis="10%">Double OT</Box>
            <Box flexBasis="20%">Bonus</Box>
            {new Date().getTime() > periodEnd && (
              <>
                <Box flexBasis="15%">Signed</Box>
                {!isMe && <Box flexBasis="15%">Include</Box>}
              </>
            )}
          </Flex>
          {Object.keys(staff).map((name, index) => {
            const staffMember = staff[name];
            const pay = payroll.find((p) => p.uid === staffMember.uid);
            const isSignedStaff = pay?.signature;
            const canDownload = new Date().getTime() > periodEnd;
            return (
              <Flex key={index} p={2} alignItems="center">
                <Box flexBasis="20%">{name}</Box>
                <Box flexBasis="10%">
                  {staffMember.exempt
                    ? 'N/A'
                    : (staffMember.hours / 60).toFixed(2)}
                </Box>
                <Box flexBasis="10%">
                  {staffMember.exempt
                    ? 'N/A'
                    : (staffMember.ot / 60).toFixed(2)}
                </Box>
                <Box flexBasis="10%">
                  {staffMember.exempt
                    ? 'N/A'
                    : (staffMember.ott / 60).toFixed(2)}
                </Box>
                <Box flexBasis="20%">{staffMember.bonus || '0.00'}</Box>
                {canDownload && (
                  <>
                    <Box flexBasis="15%">
                      {isSignedStaff ? (
                        <Link
                          href={`/payroll/me/${pay.period}?bid=${bid}&_uid=${staffMember.uid}`}
                          target="_blank"
                        >
                          Signed at {moment(pay.updated).format('MM-DD h:mm a')}
                        </Link>
                      ) : staffMember.exempt ? (
                        'Exempt'
                      ) : !pay ? (
                        <Button
                          size="sm"
                          onClick={() => requestSignature(staffMember.uid)}
                        >
                          Ask for signature
                        </Button>
                      ) : (
                        <Text>
                          Asked on {moment(pay.updated).format('MM-DD h:mm a')}
                        </Text>
                      )}
                    </Box>
                    {!isMe && (
                      <Flex flexBasis="15%">
                        {!staffMember.exempt && (
                          <Checkbox
                            isChecked={selectedStaff[staffMember.uid] || false}
                            isDisabled={!isSignedStaff}
                            onChange={() =>
                              handleStaffSelection(staffMember.uid)
                            }
                          />
                        )}
                      </Flex>
                    )}
                  </>
                )}
              </Flex>
            );
          })}
          {/* Download CSV Link */}
          {new Date().getTime() > periodEnd && csvData && !isMe && (
            <Box textAlign="right" mt={4}>
              <Link href={csvData} download="payroll.csv">
                Download CSV
              </Link>
            </Box>
          )}
        </Box>
      )}

      {/* Signature Pad for Staff */}
      {isMe && new Date().getTime() > periodEnd && (
        !isSigned ? (
          <Box mt={4}>
            <Text mb={2}>Signature:</Text>
            <canvas
              ref={signatureCanvasRef}
              style={{
                border: '1px solid #000',
                width: '100%',
                height: '200px',
                backgroundColor: 'white',
              }}
            />
            <Flex mt={2} mb={100} justifyContent="space-between">
              <Button
                onClick={handleClearSignature}
                colorScheme="red"
                isDisabled={isSignatureEmpty}
              >
                Clear
              </Button>
              <Button
                onClick={handleSaveSignature}
                colorScheme="blue"
                isDisabled={isSignatureEmpty || isLoadingSignature}
                isLoading={isLoadingSignature}
              >
                Save
              </Button>
            </Flex>
          </Box>
        ) : (
          <Box
            overflowY="auto"
            maxHeight="100vh"
            width="100%"
            position="relative"
          >
            <Flex
              justifyContent="center"
              alignItems="center"
              paddingTop="40px"
              paddingBottom="40px"
              bg="gray.100"
            >
              <Box
                p={8}
                bg="white"
                boxShadow="md"
                borderRadius="md"
                textAlign="center"
              >
                <Heading as="h2" size="lg" mb={4}>
                  Thank you
                </Heading>
                <Text>This timesheet is now signed.</Text>
              </Box>
            </Flex>
          </Box>
        )
      )}

      {/* Edit Attendance Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Adjust</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl>
              <FormLabel>Name</FormLabel>
              <Text>{currentAttendance && currentAttendance.name}</Text>
            </FormControl>
            <FormControl>
              <FormLabel>In</FormLabel>
              <Input
                value={clockedin}
                onChange={(e) => setClockedin(e.target.value)}
                placeholder="h:mm am/pm"
              />
            </FormControl>
            <FormControl>
              <FormLabel>Out</FormLabel>
              <Input
                value={clockedout}
                onChange={(e) => setClockedout(e.target.value)}
                placeholder="h:mm am/pm"
              />
            </FormControl>
            <FormControl>
              <FormLabel>Note</FormLabel>
              <Input value={note} onChange={(e) => setNote(e.target.value)} />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="ghost"
              onClick={() => setIsEditModalOpen(false)}
            >
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleUpdate} mr={2}>
              Update
            </Button>
            <Button colorScheme="red" onClick={handleDelete}>
              Delete
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Add Attendance Modal */}
      <Modal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add Attendance</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text mb={2}>
              You are about to add an ad-hoc entry into the timesheet for{' '}
              {selectedDateForAdd}.
            </Text>
            <FormControl>
              <FormLabel>Staff Member</FormLabel>
              <Select
                options={staffList.map((staff) => ({
                  value: staff._id,
                  label: staff.profile.name,
                }))}
                value={staffList
                  .filter((staff) => staff._id === selectedStaffId)
                  .map((staff) => ({
                    value: staff._id,
                    label: staff.profile.name,
                  }))}
                onChange={(option) =>
                  setSelectedStaffId(option ? option.value : '')
                }
                placeholder="Select staff member"
                isClearable
              />
            </FormControl>
            <FormControl>
              <FormLabel>In</FormLabel>
              <Input
                value={clockedin}
                onChange={(e) => setClockedin(e.target.value)}
                placeholder="h:mm am/pm"
              />
            </FormControl>
            <FormControl>
              <FormLabel>Out</FormLabel>
              <Input
                value={clockedout}
                onChange={(e) => setClockedout(e.target.value)}
                placeholder="h:mm am/pm"
              />
            </FormControl>
            <FormControl>
              <FormLabel>Note</FormLabel>
              <Input value={note} onChange={(e) => setNote(e.target.value)} />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="ghost"
              onClick={() => setIsAddModalOpen(false)}
            >
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleAddAttendance}>
              Add
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default TimeCards;
